/**
 * Pull a pasted hero image URL into our own Storage on save — the hero
 * counterpart of `resolveLogoToStorage` (firebase.ts), so a brand or
 * manufacturer stops depending on the source site staying up (the reason
 * reference images moved to Storage in #657).
 *
 * The two entities store a hero differently, and this keeps each as it was:
 * a brand's is a Storage PATH (`brands/{id}/hero/…`, what the uploader writes
 * and the app resolves), a manufacturer's a download URL.
 *
 * Unlike the logo, a failed fetch does NOT clear the field: it keeps the
 * external URL it had and reports why. A hero that already pointed at a site
 * that blocks us must not vanish because someone saved an unrelated field.
 */

export interface HeroImageValues {
  heroImage: string;
  heroImageSourceUrl: string;
}

export interface ResolvedHeroImage extends HeroImageValues {
  /** Set when an external URL could not be fetched and was kept as-is. */
  fetchError?: string;
}

export interface HeroImageResolveDeps {
  fetchBlob: (url: string) => Promise<Blob>;
  /** Upload under the entity's hero folder; returns what the doc stores. */
  upload: (blob: Blob, fileName: string) => Promise<string>;
  /** Our own Storage download URL? */
  isOurStorageUrl: (url: string) => boolean;
}

const isExternalUrl = (value: string): boolean => /^https?:\/\//i.test(value);

export async function resolveHeroImage(
  params: { input: string; manualSourceUrl?: string; prev?: Partial<HeroImageValues> },
  deps: HeroImageResolveDeps,
): Promise<ResolvedHeroImage> {
  const input = (params.input || '').trim();
  const manualSourceUrl = (params.manualSourceUrl || '').trim();
  const prevHero = (params.prev?.heroImage || '').trim();
  const prevSource = (params.prev?.heroImageSourceUrl || '').trim();

  // 1. Cleared. The uploader already deleted the object it replaced.
  if (!input) return { heroImage: '', heroImageSourceUrl: manualSourceUrl };

  // 2. Already ours — a brand Storage path, or one of our download URLs.
  if (!isExternalUrl(input) || deps.isOurStorageUrl(input)) {
    return { heroImage: input, heroImageSourceUrl: manualSourceUrl || prevSource };
  }

  // 3. Same external source as before and we already hold our own copy.
  const prevIsOurs = prevHero !== '' && (!isExternalUrl(prevHero) || deps.isOurStorageUrl(prevHero));
  if (prevIsOurs && prevSource === input) {
    return { heroImage: prevHero, heroImageSourceUrl: input };
  }

  // 4. A new external URL → fetch it and store our own copy.
  let blob: Blob;
  try {
    blob = await deps.fetchBlob(input);
  } catch (err) {
    return {
      heroImage: input,
      heroImageSourceUrl: manualSourceUrl || input,
      fetchError: err instanceof Error ? err.message : String(err),
    };
  }
  if (blob.type && !blob.type.startsWith('image/')) {
    return {
      heroImage: input,
      heroImageSourceUrl: manualSourceUrl || input,
      fetchError: `That URL returned ${blob.type}, not an image`,
    };
  }
  const fileName = input.split('/').pop()?.split('?')[0] || 'hero.jpg';
  const stored = await deps.upload(blob, fileName);
  return { heroImage: stored, heroImageSourceUrl: input };
}
